import { canOpenSettings, hasWorkspacePermission } from "./access-control";
import type { AccessUser } from "../types/api";

type RouteAccess = Pick<AccessUser, "roles" | "permissions" | "isSuperAdmin">;

export interface RouteAccessRequirement {
  path: string;
  title: string;
  anyOf: string[];
  roleAlternative?: string;
}

// Read capabilities match the API, including personal-scope alternatives. More
// specific routes must win (programme forms do not require general tools access).
const requirements: RouteAccessRequirement[] = [
  { path: "/", title: "Dashboard", anyOf: ["dashboard.read"] },
  { path: "/employees", title: "Employees", anyOf: ["employees.read"] },
  { path: "/roles", title: "Roles & access", anyOf: ["roles.manage"] },
  { path: "/crm", title: "CRM & clients", anyOf: ["clients.read"] },
  { path: "/outreach", title: "Batch email", anyOf: ["outreach.manage"] },
  { path: "/programmes", title: "Programmes", anyOf: ["programmes.read"] },
  { path: "/tprs", title: "TPRS meetings", anyOf: ["tprs.manage"] },
  {
    path: "/tasks",
    title: "Weekly tasks",
    anyOf: ["tasks.own.read", "tasks.read.all", "tasks.manage"],
  },
  {
    path: "/weekly-reports",
    title: "Weekly reports",
    anyOf: [
      "weekly_reports.own.manage",
      "weekly_reports.read.all",
      "weekly_reports.review",
    ],
  },
  {
    path: "/leave",
    title: "Leave",
    anyOf: ["leave.own.manage", "leave.read.all", "leave.review"],
  },
  {
    path: "/attendance",
    title: "Attendance",
    anyOf: ["attendance.own.manage", "attendance.read", "attendance.manage"],
  },
  { path: "/eap", title: "Employee support", anyOf: ["eap.read"] },
  {
    path: "/admin",
    title: "Administration",
    anyOf: [
      "admin.procurement.own.manage",
      "admin.procurement.read",
      "admin.procurement.review",
      "admin.assets.read",
      "attendance.own.manage",
      "attendance.read",
      "attendance.manage",
    ],
  },
  { path: "/admin/audit-logs", title: "Audit logs", anyOf: ["audit.read"] },
  {
    path: "/documents",
    title: "Resources",
    anyOf: ["documents.own.manage", "documents.read", "documents.manage"],
  },
  { path: "/qr-studio", title: "QR Studio", anyOf: ["tools.use"] },
  { path: "/tools", title: "Office tools", anyOf: ["tools.use"] },
  {
    path: "/tools/programme-forms",
    title: "Programme forms",
    anyOf: ["programmes.forms.read"],
  },
  { path: "/reports", title: "Reports", anyOf: ["reports.read"] },
  { path: "/ai", title: "WINBOX AI", anyOf: ["ai.use"] },
  {
    path: "/invoices",
    title: "Invoices",
    anyOf: ["finance.read", "finance.manage"],
  },
  {
    path: "/settings",
    title: "Settings",
    anyOf: ["settings.manage", "finance.read", "finance.manage"],
    roleAlternative:
      "General Manager or Chief Executive Officer for task reminder settings",
  },
];

export function getRouteAccessRequirement(
  pathname: string,
): RouteAccessRequirement | undefined {
  const path = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  // These two legacy list URLs redirect to Weekly Tasks; detail URLs keep
  // their own weekly-report authorization.
  if (path === "/weekly-reports" || path === "/weekly-reports/review") {
    return requirements.find((requirement) => requirement.path === "/tasks");
  }
  return requirements
    .filter(
      (requirement) =>
        path === requirement.path ||
        (requirement.path !== "/" && path.startsWith(`${requirement.path}/`)),
    )
    .sort((left, right) => right.path.length - left.path.length)[0];
}

export function canAccessRoute(
  access: RouteAccess | null | undefined,
  pathname: string,
): boolean {
  if (!access) return false;
  const requirement = getRouteAccessRequirement(pathname);
  if (!requirement) return true;
  if (requirement.path === "/settings") return canOpenSettings(access);
  return requirement.anyOf.some((permission) =>
    hasWorkspacePermission(access, permission),
  );
}

/** Keep a matching profile mounted during refocus/token-refresh work. */
export function profileRouteState(
  sessionUserId: string | null,
  accessAuthUserId: string | undefined,
  loading: boolean,
) {
  if (sessionUserId && accessAuthUserId === sessionUserId) return "ready";
  if (loading) return "loading";
  if (!sessionUserId) return "signed-out";
  return "unavailable";
}

export function permissionRouteState(
  access: (RouteAccess & Pick<AccessUser, "authUserId">) | null | undefined,
  loading: boolean,
  pathname: string,
  sessionUserId: string | null,
) {
  const profile = profileRouteState(sessionUserId, access?.authUserId, loading);
  if (profile === "loading") return "loading";
  if (profile !== "ready") return "unavailable";
  return canAccessRoute(access, pathname) ? "allowed" : "denied";
}

export function getAllowedRecoveryRoute(
  access: RouteAccess | null | undefined,
  pathname: string,
) {
  const choices = [
    { path: "/tasks", label: "Weekly tasks" },
    { path: "/tprs", label: "TPRS meetings" },
    { path: "/", label: "Dashboard" },
    { path: "/notifications", label: "Notifications" },
  ];
  return choices.find(
    (choice) => choice.path !== pathname && canAccessRoute(access, choice.path),
  );
}
